import { useEffect } from 'react';

/**
 * Paragraph-pin synchronisation for the Chapter Reader live-session view.
 *
 * Rather than continuously mirroring the teacher's scroll position (fragile
 * across viewport / font / zoom differences, and easy to fight), the teacher
 * *intentionally* pins or advances to a paragraph: "everyone look here". The
 * pinned paragraph is identified by its `data-paragraph-index` (the chapter
 * HTML is pre-tagged on every block by ChapterHtmlParagraphIndexer), so the
 * pin resolves to the same passage on every screen regardless of layout. Each
 * follower scrolls that paragraph to the top and highlights it.
 *
 * Under hard lock the student's scroller is `overflow: hidden` so they can't
 * scroll away between pins — programmatic scroll still works on an element
 * scroller (setting scrollTop ignores overflow:hidden).
 */

// Resolve the element that actually scrolls. Prefer an explicit ref (the
// dedicated reader container); otherwise walk up from the content element to
// the nearest scrollable ancestor, falling back to the document scroller.
export function resolveScroller(scrollerRef, contentRef) {
  if (scrollerRef?.current) return scrollerRef.current;
  let node = contentRef?.current || null;
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

export function isWindowScroller(el) {
  return el === document.scrollingElement || el === document.documentElement || el === document.body;
}

// Viewport-relative Y of the scroller's top edge. 0 for the window; the
// element's own top for a nested container.
function scrollerTopY(el) {
  return isWindowScroller(el) ? 0 : el.getBoundingClientRect().top;
}

// The sorted list of paragraph indices present in the content element.
export function paragraphIndices(contentEl) {
  if (!contentEl) return [];
  return Array.from(contentEl.querySelectorAll('[data-paragraph-index]'))
    .map((p) => Number(p.dataset.paragraphIndex))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
}

// The paragraph index currently at the top of the scroller — i.e. the first
// block whose bottom edge is below the scroller's top edge.
export function topParagraphIndex(scrollerEl, contentEl) {
  if (!contentEl) return null;
  const top = scrollerTopY(scrollerEl);
  for (const p of contentEl.querySelectorAll('[data-paragraph-index]')) {
    const r = p.getBoundingClientRect();
    if (r.bottom > top + 1) return Number(p.dataset.paragraphIndex);
  }
  return null;
}

// The next/prev existing paragraph index relative to `current` (step ±1 in the
// list of present indices), clamped to the ends.
export function stepParagraphIndex(contentEl, current, dir) {
  const list = paragraphIndices(contentEl);
  if (list.length === 0) return current;
  if (current == null) return dir > 0 ? list[0] : list[list.length - 1];
  const i = list.indexOf(current);
  if (i < 0) {
    // `current` isn't a real block — snap to the nearest in the step direction.
    const next = dir > 0 ? list.find((n) => n > current) : [...list].reverse().find((n) => n < current);
    return next ?? current;
  }
  const j = Math.min(list.length - 1, Math.max(0, i + (dir > 0 ? 1 : -1)));
  return list[j];
}

// Scroll so the given paragraph sits at the top of the scrollport.
// Uses scrollIntoView, which finds and scrolls the actual scroll container(s)
// — robust to layout/height assumptions and to a hard-locked (overflow:hidden)
// container, which is still programmatically scrollable.
export function scrollParagraphToTop(scrollerEl, contentEl, para, behavior = 'smooth') {
  const root = contentEl || scrollerEl;
  if (!root || para == null) return;
  const el = root.querySelector(`[data-paragraph-index="${para}"]`);
  if (!el) return;
  el.scrollIntoView({ block: 'start', behavior });
}

/**
 * Follower-side: scroll to the pinned paragraph whenever it changes. `pin` is
 * `{ para, ts }` — `ts` makes each receive a fresh object so a repeat pin still
 * re-applies. Waits a frame so a just-mounted chapter has laid out.
 */
export function usePinFollow(pin, enabled, contentRef, scrollerRef) {
  useEffect(() => {
    if (!enabled || !pin || typeof pin.para !== 'number') return;
    const scroller = resolveScroller(scrollerRef, contentRef);
    const apply = () => scrollParagraphToTop(scroller, contentRef?.current, pin.para, 'smooth');
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [pin, enabled, contentRef, scrollerRef]);
}

/**
 * Student-side hard lock: while `locked`, set the scroller to
 * `overflow-y: hidden` so the student can't scroll away between pins.
 * `signal` (e.g. the mirrored chapter id) re-applies the lock when the scroll
 * container (re)mounts on a chapter change.
 */
export function useReaderLock(contentRef, locked, enabled, scrollerRef, signal) {
  useEffect(() => {
    if (!enabled) return undefined;
    const scroller = resolveScroller(scrollerRef, contentRef);
    if (!scroller || isWindowScroller(scroller)) return undefined;
    const prev = scroller.style.overflowY;
    scroller.style.overflowY = locked ? 'hidden' : 'auto';
    return () => { scroller.style.overflowY = prev; };
  }, [contentRef, scrollerRef, locked, enabled, signal]);
}
